import { isDesktop } from "./desktop.ts";
import { currentViewAs } from "./view-as.ts";

/* ──────────────────────────────────────────────────────────────────────────
   Every request the web app makes.

   Non-GET requests carry X-Signet: 1, which the API requires as its CSRF
   check. The desktop shell adds X-Signet-Client so its sign-in is recorded
   as a desktop session, and signing out of a browser leaves it alone.
   A 401 anywhere means the session is gone, and the app goes to sign-in.
   ────────────────────────────────────────────────────────────────────────── */

export class ApiError extends Error {
  status: number;
  constructor(status: number, message: string) {
    super(message);
    this.status = status;
  }
}

export const SIGNED_OUT_EVENT = "signet:signed-out";

function headers(method: string, hasBody: boolean): Record<string, string> {
  const viewAs = currentViewAs();
  return {
    accept: "application/json",
    ...(hasBody ? { "content-type": "application/json" } : {}),
    ...(method !== "GET" ? { "x-signet": "1" } : {}),
    ...(isDesktop ? { "x-signet-client": "desktop" } : {}),
    ...(viewAs ? { "x-signet-view-as": `${viewAs.kind}:${viewAs.id}` } : {}),
  };
}

export async function api<T = unknown>(path: string, init: { method?: string; body?: unknown; signal?: AbortSignal } = {}): Promise<T> {
  const method = init.method ?? (init.body === undefined ? "GET" : "POST");
  if (currentViewAs() && method !== "GET") {
    throw new ApiError(403, "Preview mode is read only. Leave preview to make changes.");
  }
  let response: Response;
  try {
    response = await fetch(path, {
      method,
      headers: headers(method, init.body !== undefined),
      body: init.body === undefined ? undefined : JSON.stringify(init.body),
      credentials: "same-origin",
      signal: init.signal,
    });
  } catch {
    throw new ApiError(0, "Can't reach the Signet server. Check your connection.");
  }
  const text = await response.text();
  let data: any = null;
  try {
    data = text ? JSON.parse(text) : null;
  } catch {
    data = null;
  }
  if (!response.ok) {
    if (response.status === 401 && !path.startsWith("/api/auth/")) window.dispatchEvent(new Event(SIGNED_OUT_EVENT));
    throw new ApiError(response.status, data?.error ?? `The server answered ${response.status}.`);
  }
  return data as T;
}

/** A POST whose answer arrives as server-sent events, one JSON object each. */
export async function* stream<T = unknown>(path: string, body: unknown, signal?: AbortSignal): AsyncGenerator<T> {
  const response = await fetch(path, {
    method: "POST",
    headers: { ...headers("POST", true), accept: "text/event-stream" },
    body: JSON.stringify(body),
    credentials: "same-origin",
    signal,
  });
  if (!response.ok || !response.body) {
    let message = `The server answered ${response.status}.`;
    try {
      message = (await response.json()).error ?? message;
    } catch {
      /* keep the status line */
    }
    throw new ApiError(response.status, message);
  }
  const reader = response.body.pipeThrough(new TextDecoderStream()).getReader();
  let buffer = "";
  for (;;) {
    const { value, done } = await reader.read();
    if (done) break;
    buffer += value;
    let boundary = buffer.indexOf("\n\n");
    while (boundary !== -1) {
      const chunk = buffer.slice(0, boundary);
      buffer = buffer.slice(boundary + 2);
      const data = chunk
        .split("\n")
        .filter((line) => line.startsWith("data:"))
        .map((line) => line.slice(5).trimStart())
        .join("\n");
      if (data) yield JSON.parse(data) as T;
      boundary = buffer.indexOf("\n\n");
    }
  }
}
